<template>
  <q-page class="flex column page-layout">
    <q-inner-loading :showing="visible" class="fullscreen">
      <q-spinner size="50px" color="secondary" />
    </q-inner-loading>
    <section
      v-for="(item, index) in gameContentBuilder"
      :key="index"
      class="game-layout"
    >
      <article class="game-article">
        <div class="game-article__image">
          <div
            v-if="item.GameImage"
            class="game-article__background-image"
            :style="{ 'background-image': 'url(' + item.GameImage.url + ')' }"
          />
          <div class="game-article__mask" />
          <q-img
            v-if="item.GameImage"
            :src="item.GameImage.url"
            :alt="item.GameImage.alternativeText"
            :title="item.GameImage.alternativeText"
            contain
          />
          <div class="game-article__image game-article__image--no-image" v-else>
            <q-icon size="lg" name="broken_image" class="q-mb-sm" />
            <span>No image available</span>
          </div>
        </div>

        <header class="game-article__heading">
          <q-btn
            unelevated
            round
            outline
            dense
            icon="chevron_left"
            @click="$router.back()"
            class="back-button"
            size="sm"
          />
          <h1>{{ item.GameName }}<sup>TM</sup></h1>
        </header>
        <div class="game-article__play">
          <q-btn
            :disable="!item.resolvedDemoUrl"
            :color="item.resolvedDemoUrl ? 'secondary' : 'grey'"
            class="game-article__button full-width"
            unelevated
            label="Play Demo Game"
            @click="
              openGameDemo(
                item.resolvedDemoUrl,
                item.openDemoInIframe,
                item.GameName
              )
            "
          />
        </div>

        <div class="game-article__body">
          <h2>Game Description</h2>
          <span v-html="gameDescription" />
        </div>

        <div class="game-article__media">
          <q-btn
            :disable="!item.CertificateLink"
            :color="item.CertificateLink ? 'secondary' : 'grey'"
            class="game-article__button q-ma-sm"
            outline
            label="Certificates"
            @click="openContentDialog(item.CertificateLink, 'Certificates')"
            no-caps
          />
          <q-btn
            :disable="!item.MediaPackLink"
            :color="item.MediaPackLink ? 'secondary' : 'grey'"
            class="game-article__button q-ma-sm"
            outline
            label="Media Assets"
            @click="openContentDialog(item.MediaPackLink, 'Media Assets')"
            no-caps
          />
        </div>
      </article>

      <aside class="sidebar-container">
        <div class="sidebar-game-info">
          <div class="sidebar-container__heading">Game Details</div>
          <!-- <div class="sidebar-game-info__mini-game"></div> -->
          <div class="sidebar-game-info__stat" v-if="item.ReleaseDate">
            <div class="subheading">Release Date</div>
            <span class="value">{{ formatDate(item.ReleaseDate) }}</span>
          </div>
          <div class="sidebar-game-info__stat" v-if="item.DefBet">
            <div class="subheading">Default Bet</div>
            <span class="value">{{ formatCurrencyDecimal(item.DefBet) }}</span>
          </div>
          <div class="sidebar-game-info__stat" v-if="item.MinBet">
            <div class="subheading">Minimum Total Bet</div>
            <span class="value">{{ formatCurrencyDecimal(item.MinBet) }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Maximum Total Bet</div>
            <span class="value">{{ formatCurrencyDecimal(item.MaxBet) }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Paylines</div>
            <span class="value">{{ item.Lines }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Game Layout</div>
            <span class="value">{{ item.GameLayout }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Max Multiplier</div>
            <span class="value">{{
              formatStringCurrencyDecimal(item.Multiplier)
            }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Max Exposure</div>
            <span class="value">{{
              formatCurrencyDecimal(item.MaxExposure)
            }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">RTP</div>
            <span class="value">{{ item.RTP }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Volatility</div>
            <span class="value">{{ formatTextLabel(item.Volatility) }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Free Spins</div>
            <span class="value">{{ trueFalse(item.FreeSpins) }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Bonus Buy</div>
            <span class="value">{{ trueFalse(item.BonusBuy) }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Jackpot Contribution</div>
            <span class="value">{{ item.JackpotContribution }}</span>
          </div>
        </div>
        <div class="sidebar-game-info">
          <div class="sidebar-container__heading">More Info</div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Provider</div>
            <span class="value">{{ item.game_provider.ProviderName }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Game Tag</div>
            <span class="value">{{ item.GameTag }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Markor Game ID</div>
            <span class="value">{{ item.MarkorGameID }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Third Party Game ID</div>
            <span class="value">{{ item.ThirdPartyGameID }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Aggregator</div>
            <span class="value">{{ item.aggregator.Name }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Aspects</div>
            <div class="list">
              <span v-for="(aspect, index) in item.game_aspects" :key="index">{{
                aspect.Title
              }}</span>
            </div>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Launch Type</div>
            <div class="list">
              <span v-for="(type, index) in item.launch_types" :key="index">{{
                type.Name
              }}</span>
            </div>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Markets</div>
            <div class="list">
              <span v-for="(type, index) in item.markets" :key="index">{{
                type.Name
              }}</span>
            </div>
          </div>

          <div class="sidebar-game-info__stat">
            <div class="subheading">UKGC Game Register</div>
            <span class="value">{{ item.UKGCGameRegister }}</span>
          </div>
          <div class="sidebar-game-info__stat">
            <div class="subheading">Rating</div>
            <div class="stars">
              <div class="one-star" v-if="item.Rating === 'One'">
                <q-icon class="text-secondary" name="star_rate" size="sm" />
              </div>
              <div class="two-star" v-if="item.Rating === 'Two'">
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
              </div>
              <div class="three-star" v-if="item.Rating === 'Three'">
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
              </div>
              <div class="four-star" v-if="item.Rating === 'Four'">
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
              </div>
              <div class="five-star" v-if="item.Rating === 'Five'">
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
                <q-icon class="text-secondary" name="star_rate" size="sm" />
              </div>
            </div>
          </div>

          <div class="sidebar-game-info__stat">
            <div class="subheading">Available on</div>
            <div class="devices">
              <span
                v-for="(device, index) in item.device_availabilities"
                :key="index"
              >
                <q-icon
                  v-if="device.Device === 'Desktop'"
                  class="text-secondary"
                  name="desktop_windows"
                  size="sm"
                />
                <q-icon
                  v-if="device.Device === 'Tablet'"
                  class="text-secondary"
                  name="tablet_mac"
                  size="sm"
                />
                <q-icon
                  v-if="device.Device === 'Mobile'"
                  class="text-secondary"
                  name="smartphone"
                  size="sm"
                />
              </span>
            </div>
          </div>
        </div>
      </aside>
      <Dialog
        :open.sync="dialogDropboxOpen"
        :title="dialogDropboxTitle"
        :fullscreen="true"
        :persistent="true"
      >
        <DropboxEmbed :url="dialogDropboxUrl" />
      </Dialog>
      <IframeDialog
        :open.sync="dialogGameDemoIframeOpen"
        :url="dialogGameDemoUrl"
        :title="dialogGameDemoTitle"
      />
    </section>
  </q-page>
</template>

<script>
import showdown from "showdown";
import Dialog from "@/components/dialog/Dialog.vue";
import DropboxEmbed from "@/components/other/DropboxEmbed.vue";
import { formatDate } from "@/utils/formatDate";
import { generateDemoGameUrl } from "@/utils/formatGameUrl";
import {
  formatStringCurrencyDecimal,
  formatCurrencyDecimal,
} from "@/utils/formatCurrency";
import { formatTextLabel } from "@/utils/formatText";
import { trueFalse } from "@/utils/formatBoolean";
import IframeDialog from "@/components/dialog/Iframe.vue";
export default {
  name: "SingleGame",
  data() {
    return {
      game: [],
      gameDescription: "",
      visible: false,
      gameId: this.$route.params.id,
      url: this.$route.name,

      dialogDropboxOpen: false,
      dialogDropboxUrl: "",
      dialogDropboxTitle: "",

      dialogGameDemoIframeOpen: false,
      dialogGameDemoUrl: "",
      dialogGameDemoTitle: "",
    };
  },
  components: {
    Dialog,
    DropboxEmbed,
    IframeDialog,
  },
  methods: {
    generateDemoGameUrl,
    formatDate,
    formatStringCurrencyDecimal,
    formatCurrencyDecimal,
    formatTextLabel,
    trueFalse,
    async fetchGame() {
      this.visible = true;
      // Add route paramater id to the payload to perform game item fetch
      const payload = {
        link: "game-infos/" + this.gameId,
      };

      await this.$store
        .dispatch("fetchStrapiContent", payload)
        .then((response) => {
          // Assign response to this.gameDescription to allow markup to look through objects
          if (response !== undefined) {
            this.game = Array(response.data);
            // We need to convert the description to allow for markup - using showdown library
            const converter = new showdown.Converter(),
              markdown = response.data.Description;
            let content = converter.makeHtml(markdown);
            this.gameDescription = content;
            this.visible = false;
          }
        })
        .catch((error) => {
          this.errors.push(error);
        });
    },
    openContentDialog(url, title) {
      this.dialogDropboxOpen = true;
      this.dialogDropboxTitle = title;
      this.dialogDropboxUrl = url;
    },
    openGameDemo(url, demoIframeEnabled, name) {
      if (!demoIframeEnabled) {
        window.open(url, "_blank");
      }
      if (demoIframeEnabled) {
        this.dialogGameDemoIframeOpen = true;
        this.dialogGameDemoTitle = name + " Demo";
        this.dialogGameDemoUrl = url;
      }
    },
  },

  mounted() {
    this.fetchGame();
  },

  computed: {
    gameContentBuilder() {
      return this.game.map((item) => {
        return {
          ...item,
          resolvedDemoUrl: generateDemoGameUrl(
            item.game_provider.GameProviderURL,
            item.GameDemoLink
          ),
          openDemoInIframe: item.game_provider.LaunchGameDemoInIframe || false,
        };
      });
    },
  },
};
</script>
